import { ERROR_CODES, type MeResponse } from '@tk/shared';
import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import PakotettuSalasananVaihto from '../routes/PakotettuSalasananVaihto';
import { api, ApiError } from './api';
import { useKaannos } from './i18n';
import { asetaValuutta } from './valuutta';

export function useMeQuery(): UseQueryResult<MeResponse, ApiError> {
  return useQuery<MeResponse, ApiError>({
    queryKey: ['me'],
    queryFn: async () => {
      const me = await api.get<MeResponse>('/api/me');
      // Valuutta on järjestelmäasetus, ja rahan muotoilu tarvitsee sen
      // ennen ensimmäistä piirtoa. Asetus tässä eikä efektissä: efekti
      // ajetaan vasta renderöinnin jälkeen, jolloin ensimmäinen näkymä
      // ehtisi näyttää luvut väärässä valuutassa.
      asetaValuutta(me.settings.currency);
      return me;
    },
    // 401 ei ole virhe jota kannattaa yrittää uudelleen — se on
    // uloskirjautunut tila.
    retry: (failureCount, error) => !error.isUnauthenticated && failureCount < 2,
    staleTime: 5 * 60 * 1000,
    // Välilehdelle palatessa tarkistetaan, onko istunto yhä voimassa, jotta
    // päättynyt istunto ohjaa kirjautumiseen heti eikä vasta ensimmäisestä
    // epäonnistuvasta latauksesta. staleTime pitää tarkistukset harvoina.
    refetchOnWindowFocus: true,
  });
}

/**
 * Istunto voi päättyä kesken käytön: se vanhenee, käyttäjä kirjautuu ulos
 * toisessa välilehdessä tai ylläpito poistaa sen. Silloin jokainen kysely
 * saa 401:n, mutta välimuistissa oleva `me` pitää sovelluksen auki, ja
 * näkymät jäisivät näyttämään latausvirheitä. Tämä käsittelijä kytketään
 * QueryClientin kyselyjen ja mutaatioiden virheisiin ja vie
 * kirjautumissivulle, josta palataan samaan näkymään.
 *
 * Koko sivun lataus eikä reitittimen siirtymä: se tyhjentää edellisen
 * istunnon välimuistin varmasti, samoin kuin uloskirjautuminen.
 */
export function istunnonPaattymisenKasittelija(queryClient: QueryClient) {
  let ohjattu = false;
  return (error: unknown) => {
    if (ohjattu || !(error instanceof ApiError)) return;
    // Koodi rajaa pois muut 401:t, kuten jaetun salaisuuden PIN-kyselyn.
    if (error.code !== ERROR_CODES.UNAUTHENTICATED) return;
    // Ilman tunnettua istuntoa 401 ei tarkoita päättynyttä istuntoa vaan
    // esimerkiksi väärää salasanaa kirjautumislomakkeella. Epäonnistunut
    // `me`-haku jättää edellisen vastauksen välimuistiin, joten tämä
    // tunnistaa myös sen, kun juuri `me` saa 401:n.
    if (queryClient.getQueryData(['me']) === undefined) return;
    ohjattu = true;
    const paluu = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/kirjaudu?virhe=istunto-paattyi&paluu=${paluu}`);
  };
}

const SessionContext = createContext<MeResponse | null>(null);

export function useSession(): MeResponse {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession vaatii kirjautuneen käyttäjän. Käytä <VaatiiKirjautumisen>.');
  }
  return session;
}

export function useLogout() {
  const queryClient = useQueryClient();
  return async () => {
    await api.post('/auth/logout').catch(() => undefined);
    queryClient.clear();
    window.location.assign('/kirjaudu');
  };
}

export function VaatiiKirjautumisen({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data, isPending, error } = useMeQuery();
  const { t } = useKaannos();
  const logout = useLogout();

  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-teksti-vaimea text-sm">{t('yleinen.ladataan')}</p>
      </div>
    );
  }

  if (error?.isUnauthenticated || !data) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/kirjaudu?paluu=${from}`} replace />;
  }

  // Väliaikainen salasana ei avaa muuta kuin vaihtolomakkeen. Sama ehto on
  // palvelimella (13.2.1); tämä on se puoli, joka kertoo mitä tehdä.
  if (data.mustChangePassword) {
    return <PakotettuSalasananVaihto onLogout={() => void logout()} />;
  }

  return <SessionContext.Provider value={data}>{children}</SessionContext.Provider>;
}

/**
 * Roolisuojattu reitti. Suojaus on käytettävyyttä, ei turvaa: varsinainen
 * tarkistus tehdään jokaisessa päätteessä palvelimella (13.3). Tämä estää
 * vain sen, että käyttäjä päätyy näkymään, joka näyttäisi hänelle
 * pelkkiä virheilmoituksia.
 */
export function VaatiiOikeuden({
  oikeus,
  children,
}: {
  /** Yksi oikeus tai lista, josta riittää mikä tahansa. */
  oikeus: keyof MeResponse['permissions'] | Array<keyof MeResponse['permissions']>;
  children: ReactNode;
}) {
  const session = useSession();
  const vaaditut = Array.isArray(oikeus) ? oikeus : [oikeus];
  if (!vaaditut.some((nimi) => session.permissions[nimi])) {
    return <Navigate to="/kalenteri" replace />;
  }
  return <>{children}</>;
}
